import { createSelector } from '@reduxjs/toolkit';
import { UserActionType } from '@wix/ambassador-funnel-chatbot-v1-session/types';

import { MESSAGES } from '../../../constants';
import { getLast } from '../../../util/guards';
import { IAIArticleAnswerItem } from '../messageCreators/aiArticleAnswer';
import { TRootState } from '../reducers';
import { ChatScrollTo, IArticleState, IUserAction, RestartChatParameters, TItem } from '../reducers/types';

export const selectItems = (state: TRootState): TItem[] => {
  return state.chat.items;
};

export const selectIsMessageWasPresentOnPageLoad = (id: string) => {
  return (state: TRootState): boolean => {
    return state.chat.itemsIdsOnLastPageLoad.includes(id);
  };
};

export const selectLastItem = (state: TRootState): TItem | undefined => {
  return getLast(state.chat.items);
};

export const selectUnreadCount = (state: TRootState): number => {
  return state.chat.unreadCount;
};

export const selectIsLiveChat = (state: TRootState): boolean => {
  return state.chat.isLiveChat;
};

export const selectIsMuted = (state: TRootState): boolean => {
  return state.chat.isMuted;
};

export const selectIsLocked = (state: TRootState): boolean => {
  return state.chat.isLocked;
};

export const selectIsConnectingAgent = (state: TRootState): boolean => {
  return state.chat.isConnectingAgent === true;
};

export const selectIsReset = (state: TRootState): boolean => {
  return !!state.chat.isReset;
};

export const selectConversationId = (state: TRootState): string | undefined => {
  return state.chat.conversationId;
};

export const selectUndelivered = (id: string | undefined) => {
  return (state: TRootState): IUserAction | UserActionType | undefined => {
    return id ? state.chat.undelivered[id] : undefined;
  };
};

export const selectScrollTo = (state: TRootState): ChatScrollTo | undefined => {
  return state.chat.scrollTo;
};

export const selectRestartChatParameters = (state: TRootState): RestartChatParameters | undefined => {
  return state.chat.restartChatParameters;
};

export const selectIsUserEngaged = (state: TRootState): boolean => {
  return Boolean(state.chat.items.find(({ type }) => type === MESSAGES.USER_MESSAGE));
};

export const selectStartingNodeId = (state: TRootState): string | undefined => {
  return state.chat.startingNodeId;
};

export const selectIsFirstPriorityUser = (state: TRootState): boolean => {
  return state.chat.isFirstPriorityUser === true;
};

/**
 * Selects the last non-client-only message id
 * There are some cases when messages are being created synthetically for the UI purposes
 * We don't want to count them as a messages when it comes to polling
 * since they are not being sent to the server
 */
export const selectLastNonClientOnlyMessageId = createSelector(selectItems, (items) => {
  for (let i = items.length - 1; i >= 0; i--) {
    if (!items[i].clientOnly) {
      return items[i].id;
    }
  }
});

export const selectStartChatLoading = createSelector(
  (state: TRootState) => state.chat,
  ({ startChatLoading }) => startChatLoading,
);

export const selectStartChatError = createSelector(
  (state: TRootState) => state.chat,
  ({ startChatError }) => startChatError,
);

export const selectShouldLoadStartChat = createSelector(
  (state: TRootState) => state.chat,
  ({ reloadStartChat, startChatLoading, startChatError, items, isReset }) => {
    if (reloadStartChat) {
      return true;
    }

    if (isReset) {
      return false;
    }

    const isLoaded = !startChatLoading && !startChatError && items.length > 0;

    return !isLoaded;
  },
);

export const selectChatSessionState = (state: TRootState) => {
  return {
    conversationId: state.chat.conversationId,
    lastMessageId: selectLastNonClientOnlyMessageId(state),
    unreadCount: state.chat.unreadCount,
  };
};

export const selectArticlePreviews = (
  state: TRootState,
): Record<string, Omit<IArticleState, 'articleHeadingVisible' | 'isArticleHidden'>> | undefined => {
  return state.chat.articlePreviews;
};

export const selectIsDocumentVisible = (state: TRootState): boolean | undefined => {
  return state.chat.isDocumentVisible;
};

export const selectHasMessages = createSelector(selectItems, (items) => items.length > 0);

export const selectRelatedArticles = createSelector(selectItems, (items): IAIArticleAnswerItem[] => {
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];
    if (item.type === MESSAGES.USER_MESSAGE) {
      return [];
    }
    if (item.type === MESSAGES.AI_ARTICLE_ANSWER && 'articles' in item) {
      return item.articles;
    }
  }
  return [];
});

export const selectIsRelatedArticlesLoading = createSelector(selectItems, (items): boolean => {
  const lastItem = items[items.length - 1];
  if (!lastItem) {
    return false;
  }
  if (lastItem.type === MESSAGES.USER_MESSAGE || lastItem.type === MESSAGES.BOT_TYPING) {
    return true;
  }
  if (lastItem.type === MESSAGES.AI_ARTICLE_ANSWER && 'articles' in lastItem) {
    return Boolean(lastItem.isStreaming) && lastItem.articles.length === 0;
  }
  return false;
});

const selectAnimatingAnswerId = (state: TRootState): string | undefined => state.chat.animatingAnswerId;

export const selectIsBotStreaming = createSelector(
  [selectItems, selectAnimatingAnswerId],
  (items, animatingAnswerId): boolean => {
    if (animatingAnswerId !== undefined) {
      return true;
    }
    const lastItem = getLast(items);
    if (!lastItem) {
      return false;
    }
    if (lastItem.type === MESSAGES.BOT_TYPING) {
      return true;
    }
    if (lastItem.type === MESSAGES.AI_ARTICLE_ANSWER && 'isStreaming' in lastItem) {
      return Boolean(lastItem.isStreaming);
    }
    return false;
  },
);

export const selectIsFromAnswerGeneration = (state: TRootState): boolean => {
  return state.chat.isFromAnswerGeneration === true;
};

export const selectIsStartOverFromAnswerGeneration = (state: TRootState): boolean => {
  return state.chat.isStartOverFromAnswerGeneration === true;
};

export const selectPendingFollowUpQuestion = (state: TRootState): string | undefined =>
  state.chat.pendingFollowUpQuestion;
